<template>
    <section v-editable="blok" class="signup-section">
        <div class="signup-section__container">
            <div v-if="isSuccess" class="signup-section__success">
                {{ t('signupForm.successMessage') }}
            </div>
            <template v-else>
                <div class="signup-section__text">
                    <h3 class="signup-section__title">{{ content?.title }}</h3>
                    <RichText v-if="content?.text" :richtext="content.text" class="signup-section__descr" />
                </div>
                <div class="signup-section__form">
                    <form name="iterable_optin" class="signup-form" @submit.prevent="subscribe">
                        <input
                            id="Email"
                            v-model="emailAddress"
                            name="email"
                            type="text"
                            :placeholder="t('signupForm.emailPlaceholder')"
                            class="signup-form__input"
                            :class="{ 'is-error': isError }"
                            :disabled="isLoading"
                        />
                        <button class="form-btn" :class="{ 'is-loading': isLoading }" type="submit">
                            <span class="form-btn__loading" />
                            <span class="form-btn__text">{{ t('signupForm.subscribe') }}</span>
                        </button>
                        <span v-if="isError" class="signup-form__error">
                            {{ errorMessage }}
                        </span>
                    </form>
                </div>
            </template>
        </div>
    </section>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import type { IL4EmailSignupNew } from '~/types/components/L4EmailSignupNew';
const { t, locale, locales } = useI18n();

const localeObj = Object.values(locales.value).find((item: any) => item.code === locale.value);
const { blok } = defineProps<{ blok: IL4EmailSignupNew }>();
const { content } = blok.signup;

const emailAddress = ref<string>('');
const isError = ref<boolean>(false);
const errorMessage = ref<string>('');
const isSuccess = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const config = useRuntimeConfig();

// check if it is a valid email
const checkEmail = (value: string): boolean => {
    const regex =
        /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/g;
    return regex.test(value);
};

watch(
    () => emailAddress.value,
    () => {
        isError.value = false;
    }
);

async function subscribe() {
    isError.value = false;

    if (!checkEmail(emailAddress.value)) {
        isError.value = true;
        errorMessage.value = t('signupForm.errorMessage');
        return;
    }

    try {
        isLoading.value = true;

        const { data, error } = await useFetch('/api/subscribe', {
            headers: { 'Content-type': 'application/json' },
            method: 'POST',
            body: {
                email: emailAddress.value,
                listId: config?.public.ITERABLE_LIST_ID,
                culture: localeObj.iso,
                signupSource: blok.signupSource ?? '',
            },
        });

        if (error) {
            isError.value = true;
            if (error?.value?.statusMessage === 'invalid_email') {
                errorMessage.value = t('signupForm.errorMessage');
            }
        }
        if (data.value) {
            isSuccess.value = true;
        }
        isLoading.value = false;
    } catch (error) {
        isLoading.value = false;
    }
}
</script>

<style lang="postcss" scoped>
.signup-section {
    @apply py-8 md:py-16 bg-primary;
    &__container {
        @apply px-4 md:px-0 md:container flex flex-col md:flex-row gap-8 justify-between md:items-center flex-wrap;
    }
    &__title {
        @apply text-white text-2xl leading-7 font-bold;
    }
    &__descr {
        @apply text-white text-xl leading-7 font-normal;
    }
    &__form {
        @apply shrink-0;
    }
    &__success {
        @apply w-full text-white font-bold text-2xl leading-7 text-center;
    }
}

.signup-form {
    @apply flex flex-col md:flex-row gap-3 md:gap-2 relative;
    &__input {
        @apply rounded bg-white px-3 py-2 w-full md:w-72 h-10;
        border: 1px solid #ddd;
        background: #fff;
        box-shadow: 0px 0px 10px 1px #ddd inset;
        &.is-error {
            border-color: #ef0303;
        }
    }
    &__error {
        @apply text-center text-[#FBABAB] md:absolute top-full left-0 pt-1.5;
    }
}

.form-btn {
    @apply bg-buttongreen py-1.5 px-8 rounded hover:opacity-90 w-full md:w-auto h-10 relative;
    &__text {
        @apply block relative  text-white text-lg font-bold uppercase;
        .is-loading & {
            @apply opacity-0;
        }
    }
    &__loading {
        @apply block absolute left-1/2 top-1/2 -mt-2 -ml-2 opacity-0 w-4 h-4 mx-auto border-2 border-white border-r-transparent border-b-transparent rounded-full;
        animation: rotate 1s infinite linear;
        .is-loading & {
            @apply opacity-100;
        }
    }
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
